// @flow

import { ButtonGroup } from '@atlaskit/button';
import Button from '@atlaskit/button/standard-button';
import { FieldTextStateless as TextField } from '@atlaskit/field-text';
import { ModalFooter } from '@atlaskit/modal-dialog';
import { useMediaQuery } from '@material-ui/core';
import React, { Component } from 'react';
import type { Dispatch } from 'redux';

import { setPassword } from '../../base/conference';
import { Dialog } from '../../base/dialog';
import { translate } from '../../base/i18n';
import { setIsSwitchingBreakoutRoom } from '../../base/mtApi';
import { connect } from '../../base/redux';
import { _cancelPasswordRequiredPrompt } from '../actions';

/**
 * The ID to be used for the cancel button if enabled.
 *
 * @type {string}
 */
const CANCEL_BUTTON_ID = 'modal-dialog-cancel-button';

/**
  * The ID to be used for the ok button if enabled.
 *
  * @type {string}
  */
const OK_BUTTON_ID = 'modal-dialog-ok-button';

/**
 * The type of the React {@code Component} props of
 * {@link PasswordRequiredPrompt}.
 */
type Props = {

    /**
     * The MHConference which requires a password.
     */
    conference: Object,

    /**
     * The redux store's {@code dispatch} function.
     */
    dispatch: Dispatch<any>,

    /**
     * The translate function.
     */
    t: Function,
    password: ?string,
    renderFooterButtons: ?boolean
};

/**
 * The type of the React {@code Component} state of
 * {@link PasswordRequiredPrompt}.
 */
type State = {

    /**
     * The password entered by the local participant.
     */
    password: string
}

/**
 * Implements a React Component which prompts the user when a password is
 * required to join a conference.
 */
class PasswordRequiredPrompt extends Component<Props, State> {
    /**
     * Initializes a new PasswordRequiredPrompt instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);
        this.state = {
            password: props.password ?? ''
        };

        // Bind event handlers so they are only bound once per instance.
        this._onPasswordChanged = this._onPasswordChanged.bind(this);
        this._onCancel = this._onCancel.bind(this);
        this._onSubmit = this._onSubmit.bind(this);
        this._renderFooter = this._renderFooter.bind(this);
    }

    /**
     * When Component Mounts.
     *
     * @returns {any}
     */
    componentDidMount() {
        setTimeout(() => {
            this._onSubmit();
        }, 1000);
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { renderFooterButtons, password } = this.props;


        if (password) {
            return (<Dialog
                cancelDisabled = { true }
                className = { 'w-0 h-0' }
                disableBlanketClickDismiss = { true }
                disableFooter = { true }
                hideDialog = { true }
                isJoinMainRoomText = { true }
                isModal = { false }
                okKey = 'Join'
                onCancel = { this._onCancel }
                onSubmit = { this._onSubmit }
                width = 'small'>
                Joining into main room
            </Dialog>);
        }

        return (
            <Dialog
                cancelDisabled = { false }
                disableBlanketClickDismiss = { true }
                disableFooter = { Boolean(renderFooterButtons) }
                isModal = { false }
                okKey = { this.props.t('calendarSync.join') }
                onCancel = { this._onCancel }
                onSubmit = { this._onSubmit }
                titleKey = 'dialog.passwordRequired'
                width = 'small'>
                { this._renderBody() }
            </Dialog>
        );
    }

    /**
     * Display component in dialog body.
     *
     * @returns {ReactElement}
     * @protected
     */
    _renderBody() {
        const { renderFooterButtons = false } = this.props;

        return (
            <div>
                <TextField
                    autoFocus = { true }
                    compact = { true }
                    label = { this.props.t('dialog.passwordLabel') }
                    name = 'lockKey'
                    onChange = { this._onPasswordChanged }
                    shouldFitContainer = { true }
                    type = 'text'
                    value = { this.state.password } />
                { renderFooterButtons && this._renderFooter()}
            </div>
        );
    }

    _renderFooter: () => React$Node;

    /**
     * Returns a ReactElement to display buttons for closing the modal.
     *
     * @param {Object} propsFromModalFooter - The props passed in from the
     * {@link ModalFooter} component.
     * @private
     * @returns {ReactElement}
     */
    _renderFooter() {
        // Filter out falsy (null) values because {@code ButtonGroup} will error
        // if passed in anything but buttons with valid type props.
        const buttons = [
            this._renderOKButton(),
            this._renderCancelButton()
        ].filter(Boolean);


        return (
            <ModalFooter >
                {

                    /**
                     * Atlaskit has this empty span (JustifySim) so...
                     */
                }
                <span />
                <ButtonGroup>
                    { buttons }
                </ButtonGroup>
            </ModalFooter>

        );
    }

    /**
     * Renders Cancel button.
     *
     * @private
     * @returns {ReactElement|null} The Cancel button if enabled and dialog is
     * not modal.
     */
    _renderCancelButton() {

        return (
            <Button
                appearance = 'subtle'
                id = { CANCEL_BUTTON_ID }
                key = 'cancel'
                onClick = { this._onCancel }
                type = 'button'>
                { this.props.t('dialog.Cancel') }
            </Button>
        );
    }

    /**
         * Renders OK button.
         *
         * @private
         * @returns {ReactElement|null} The OK button if enabled.
         */
    _renderOKButton() {

        return (
            <Button
                appearance = 'primary'
                form = 'modal-dialog-form'
                id = { OK_BUTTON_ID }
                key = 'submit'
                onClick = { this._onSubmit }
                type = 'button'>
                { this.props.t('calendarSync.join') }
            </Button>
        );
    }

    _onPasswordChanged: ({ target: { value: * }}) => void;

    /**
     * Notifies this dialog that password has changed.
     *
     * @param {Object} event - The details of the notification/event.
     * @private
     * @returns {void}
     */
    _onPasswordChanged({ target: { value } }) {
        const trimmedvalue = typeof value === 'string' ? value.trim() : value;

        this.setState({
            password: trimmedvalue
        });
    }

    _onCancel: () => boolean;

    /**
     * Dispatches action to cancel and dismiss this dialog.
     *
     * @private
     * @returns {boolean}
     */
    _onCancel() {

        this.props.dispatch(
            _cancelPasswordRequiredPrompt(this.props.conference));

        return true;
    }

    _onSubmit: () => boolean;

    /**
     * Dispatches action to submit value from this dialog.
     *
     * @private
     * @returns {boolean}
     */
    _onSubmit() {
        const { conference } = this.props;

        // We received that password is required, but user is trying anyway to
        // login without a password. Mark the room as not locked in case she
        // succeeds (maybe someone removed the password meanwhile). If it is
        // still locked, another password required will be received and the room
        // again will be marked as locked.
        this.props.dispatch(
            setPassword(conference, conference.join, this.state.password));

        // We have used the password so let's clean it.
        this.setState({
            password: undefined
        });

        this.props.dispatch(setIsSwitchingBreakoutRoom(false));

        return true;
    }
}


/**
 * Maps part of the Redux state to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @returns {Props}
 */
function _mapStateToProps(state) {
    const isSwitchingBreakOutRoom = state['features/base/mtApi'].isSwitchingBreakOutRoom;

    return {
        _isSwitchingBreakOutRoom: isSwitchingBreakOutRoom
    };
}

export default translate(connect(_mapStateToProps)(props => {
    const mediaQuery = useMediaQuery('(max-width:600px)');

    return (<PasswordRequiredPrompt
        { ...props }
        renderFooterButtons = { mediaQuery } />);
}));
